import type { BattleConfig } from "../core/types";
import { isLocale, type Locale } from "../i18n";

const PREFERENCES_KEY = "egg-arena:preferences:v1";

export interface UserPreferences {
  locale: Locale;
  enemyHealthPercent: number;
  enemySpeedPercent: number;
  enemyDamagePercent: number;
  dangerLevel: 0 | 1 | 2 | 3 | 4 | 5;
  masterVolume: number;
  screenShake: boolean;
  reducedEffects: boolean;
}

export const DEFAULT_PREFERENCES: UserPreferences = {
  locale: "en",
  enemyHealthPercent: 100,
  enemySpeedPercent: 100,
  enemyDamagePercent: 100,
  dangerLevel: 0,
  masterVolume: 70,
  screenShake: true,
  reducedEffects: false,
};

export function loadPreferences(): UserPreferences {
  try {
    const encoded = window.localStorage.getItem(PREFERENCES_KEY);
    if (!encoded) return { ...DEFAULT_PREFERENCES };
    const value: unknown = JSON.parse(encoded);
    if (!isRecord(value)) return { ...DEFAULT_PREFERENCES };
    return normalizePreferences(value);
  } catch {
    return { ...DEFAULT_PREFERENCES };
  }
}

export function savePreferences(preferences: UserPreferences): void {
  try {
    window.localStorage.setItem(
      PREFERENCES_KEY,
      JSON.stringify(normalizePreferences(preferences)),
    );
  } catch {
    // Storage can be unavailable in privacy modes; the game remains playable.
  }
}

export function preferencesToBattleConfig(
  preferences: UserPreferences,
  waveSeconds: number,
  progression?: {
    unlockedDangerLevel: 0 | 1 | 2 | 3 | 4 | 5;
    unlockedWeaponIds: string[];
    unlockedItemIds: string[];
  },
): BattleConfig {
  const dangerLevel = Math.min(
    preferences.dangerLevel,
    progression?.unlockedDangerLevel ?? preferences.dangerLevel,
  ) as BattleConfig["dangerLevel"];
  return {
    waveSeconds,
    enemyHealthMultiplier: preferences.enemyHealthPercent / 100,
    enemySpeedMultiplier: preferences.enemySpeedPercent / 100,
    enemyDamageMultiplier: preferences.enemyDamagePercent / 100,
    dangerLevel,
    dynamicWaveDuration: waveSeconds === 30,
    ...(progression
      ? {
          unlockedWeaponIds: progression.unlockedWeaponIds,
          unlockedItemIds: progression.unlockedItemIds,
        }
      : {}),
  };
}

function normalizePreferences(
  value: Partial<UserPreferences> | Record<string, unknown>,
): UserPreferences {
  return {
    locale: isLocale(value.locale) ? value.locale : DEFAULT_PREFERENCES.locale,
    enemyHealthPercent: boundedNumber(value.enemyHealthPercent, 25, 200, 100),
    enemySpeedPercent: boundedNumber(value.enemySpeedPercent, 50, 150, 100),
    enemyDamagePercent: boundedNumber(value.enemyDamagePercent, 25, 200, 100),
    dangerLevel: boundedDanger(value.dangerLevel),
    masterVolume: boundedNumber(value.masterVolume, 0, 100, 70),
    screenShake:
      typeof value.screenShake === "boolean" ? value.screenShake : true,
    reducedEffects:
      typeof value.reducedEffects === "boolean" ? value.reducedEffects : false,
  };
}

function boundedDanger(value: unknown): UserPreferences["dangerLevel"] {
  return boundedNumber(value, 0, 5, 0) as UserPreferences["dangerLevel"];
}

function boundedNumber(
  value: unknown,
  minimum: number,
  maximum: number,
  fallback: number,
): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.round(Math.max(minimum, Math.min(maximum, value)))
    : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
